import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useYear } from "@/context/YearContext";
import { formatINR, formatFullINR, MONTHS } from "@/lib/format";
import { Target, Receipt, ShieldAlert } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const STATUS_STYLE = {
  SAFE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  WARNING: "bg-amber-50 text-amber-700 border-amber-200",
  CRITICAL: "bg-red-50 text-red-700 border-red-200",
  "NO BUDGET": "bg-zinc-100 text-zinc-500 border-zinc-200",
};

const KpiCard = ({ testId, label, value, sub, icon: Icon, accent = "text-blue-600", delay = 0 }) => (
  <div data-testid={testId} className="card-surface p-6 fade-up" style={{ animationDelay: `${delay}ms` }}>
    <div className="flex items-center justify-between mb-4">
      <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{label}</p>
      <Icon size={16} className={accent} />
    </div>
    <p className="font-mono-num text-2xl sm:text-3xl font-bold text-zinc-900 tracking-tight">{value}</p>
    {sub && <p className="text-xs text-zinc-500 mt-2">{sub}</p>}
  </div>
);

const FundCard = ({ testId, label, value, sub, color, delay }) => (
  <div data-testid={testId} className="card-surface p-5 fade-up border-l-2" style={{ animationDelay: `${delay}ms`, borderLeftColor: color }}>
    <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">{label}</p>
    <p className="font-mono-num text-xl font-bold text-zinc-900">{value}</p>
    <p className="text-[11px] text-zinc-500 mt-1">{sub}</p>
  </div>
);

export default function Dashboard() {
  const { year } = useYear();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/dashboard", { params: { year } });
    setData(data);
  }, [year]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-zinc-500 py-20 text-center" data-testid="dashboard-loading">Loading…</div>;

  const targetPct = data.next_year_target > 0 ? Math.min(100, (data.next_year_saved / data.next_year_target) * 100) : 0;
  const runwayPct = Math.min(100, ((data.runway_months || 0) / 12) * 100);
  const chartData = MONTHS.map((m, i) => {
    const closing = data.closings.find((c) => c.month === i + 1);
    return { month: m, Budget: data.monthly_totals[i], Actual: closing ? closing.actual_expenses : 0, Revenue: closing ? closing.revenue : 0 };
  });
  const latestClosing = data.closings.length ? data.closings[data.closings.length - 1] : null;
  const maxCat = Math.max(1, ...data.budget_vs_actual.map((c) => Math.max(c.budgeted, c.actual)));

  return (
    <div data-testid="dashboard-view-container" className="space-y-8">
      <div className="fade-up">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">Dashboard</h1>
        <p className="text-sm text-zinc-500 mt-1">Fiscal year {year} · Capital reserve overview</p>
      </div>

      <div className="card-surface p-6 sm:p-8 fade-up" data-testid="protected-runway-hero">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Protected Runway</p>
              <span data-testid="runway-status-badge" className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${STATUS_STYLE[data.runway_status]}`}>
                {data.runway_status}
              </span>
            </div>
            <p className="font-mono-num text-4xl sm:text-5xl font-extrabold text-zinc-900 tracking-tight" data-testid="kpi-runway-months">
              {data.runway_months === null ? "—" : data.runway_months.toFixed(1)}
              <span className="text-zinc-400 text-2xl font-bold"> / 12 months</span>
            </p>
            <p className="text-xs text-zinc-500 mt-2">
              Protected money (Operating + Next-Year Reserve) divided by monthly core expenses ({formatINR(data.monthly_essential_burn)}/mo). Target: 12 months.
            </p>
          </div>
          <div className="text-right space-y-1.5 min-w-[210px]">
            <div className="flex justify-between gap-6 text-sm">
              <span className="text-zinc-500">Protected capital</span>
              <span className="font-mono-num font-semibold text-zinc-900" data-testid="kpi-protected-capital">{formatINR(data.protected_capital)}</span>
            </div>
            <div className="flex justify-between gap-6 text-sm">
              <span className="text-zinc-500">12-month requirement</span>
              <span className="font-mono-num font-semibold text-zinc-900">{formatINR(data.monthly_essential_burn * 12)}</span>
            </div>
            <div className="flex justify-between gap-6 text-sm">
              <span className="text-zinc-500">Shortfall to 12 months</span>
              <span className={`font-mono-num font-bold ${data.shortfall_to_12m > 0 ? "text-red-600" : "text-emerald-600"}`} data-testid="kpi-shortfall-12m">
                {data.shortfall_to_12m > 0 ? formatINR(data.shortfall_to_12m) : "None"}
              </span>
            </div>
          </div>
        </div>
        <div className="h-2.5 rounded-full bg-zinc-100 overflow-hidden mt-5">
          <div className={`h-full rounded-full transition-all duration-700 ${data.runway_status === "SAFE" ? "bg-emerald-500" : data.runway_status === "WARNING" ? "bg-amber-500" : "bg-red-500"}`}
            style={{ width: `${runwayPct}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <KpiCard testId="kpi-next-year-target" label={`Next-Year Target (${year + 1})`} icon={Target}
          value={formatINR(data.next_year_target)}
          sub={`${data.next_year_approved_core ? "Based on approved next-year budget" : "120% rule"} · ${formatINR(data.monthly_contribution)}/month`} delay={0} />
        <KpiCard testId="kpi-tax-reserve" label="Taxes & Statutory Reserve" icon={Receipt}
          value={formatINR(data.tax_reserve)} sub="Kept aside for GST, income tax & other dues" accent="text-rose-600" delay={60} />
      </div>

      <div className="card-surface p-6 fade-up" data-testid="reserve-progress-card">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Next-Year Reserve Progress</p>
            <p className="font-mono-num text-2xl font-bold text-zinc-900 mt-1">
              {formatINR(data.next_year_saved)} <span className="text-zinc-400 text-base font-medium">/ {formatINR(data.next_year_target)}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="font-mono-num text-lg font-bold text-blue-600">{targetPct.toFixed(1)}%</p>
            <p className="text-xs text-zinc-500">Gap: {formatINR(data.next_year_gap)}</p>
          </div>
        </div>
        <div className="h-2.5 rounded-full bg-zinc-100 overflow-hidden">
          <div className="h-full rounded-full bg-blue-600 transition-all duration-700" style={{ width: `${targetPct}%` }} />
        </div>
      </div>

      <div>
        <h2 className="font-heading text-lg font-semibold text-zinc-900 mb-4">Fund Balances</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <FundCard testId="fund-card-operating" label="Operating Reserve" color="#3B82F6"
            value={formatINR(data.operating_reserve)} sub="Working capital · goes down with expenses" delay={0} />
          <FundCard testId="fund-card-next-year" label="Next-Year Reserve (NYMR)" color="#10B981"
            value={formatINR(data.next_year_saved)} sub="Funded first every month" delay={50} />
          <FundCard testId="fund-card-founder-savings" label="Founder Family Savings" color="#F59E0B"
            value={formatINR(data.founder_savings)} sub="30% after NYMR · protected" delay={100} />
          <FundCard testId="fund-card-innovation-growth" label="Innovation & Growth" color="#8B5CF6"
            value={formatINR(data.innovation_growth_balance)} sub={`What remains after NYMR & founder share · unspent c/f ${formatINR(data.unspent_growth_balance)}`} delay={150} />
          <FundCard testId="fund-card-tax" label="Taxes & Statutory" color="#F43F5E"
            value={formatINR(data.tax_reserve)} sub="Kept aside for tax payments" delay={200} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="card-surface p-6 lg:col-span-3 fade-up" data-testid="budget-vs-actual-chart">
          <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-5">Budget vs Actual · Monthly Revenue</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} barGap={2}>
              <XAxis dataKey="month" stroke="#A1A1AA" fontSize={11} tickLine={false} axisLine={{ stroke: "#E4E4E7" }} />
              <YAxis stroke="#A1A1AA" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => formatINR(v)} width={70} />
              <Tooltip
                contentStyle={{ background: "#FFFFFF", border: "1px solid #E4E4E7", borderRadius: 8, fontSize: 12, boxShadow: "0 4px 14px rgba(0,0,0,0.08)" }}
                labelStyle={{ color: "#18181B", fontWeight: 600 }}
                formatter={(v, name) => [formatINR(v), name]}
                cursor={{ fill: "rgba(59,130,246,0.05)" }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Budget" fill="#D6D3CB" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Actual" fill="#3B82F6" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Revenue" fill="#10B981" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card-surface p-6 lg:col-span-2 fade-up" data-testid="category-variance-card">
          <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-5">Budget vs Actual by Category</p>
          {data.budget_vs_actual.length === 0 ? (
            <p className="text-sm text-zinc-500">No budget data yet.</p>
          ) : (
            <div className="space-y-4">
              {data.budget_vs_actual.map((c) => (
                <div key={c.category}>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="font-medium text-zinc-700">{c.category}</span>
                    <span className="font-mono-num text-zinc-500">
                      <span className="text-blue-600 font-semibold">{formatINR(c.actual)}</span> / {formatINR(c.budgeted)}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-zinc-100 relative overflow-hidden">
                    <div className="absolute h-full rounded-full bg-zinc-300" style={{ width: `${(c.budgeted / maxCat) * 100}%` }} />
                    <div className={`absolute h-full rounded-full ${c.actual > c.budgeted ? "bg-red-500" : "bg-blue-600"}`} style={{ width: `${(c.actual / maxCat) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="card-surface p-6 lg:col-span-2 fade-up" data-testid="waterfall-latest-card">
          <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-5">Latest Allocation Waterfall</p>
          {latestClosing ? (
            <div className="space-y-1">
              <p className="font-heading font-semibold text-zinc-900 mb-2">{MONTHS[latestClosing.month - 1]} {latestClosing.year}</p>
              {[
                ["Cash revenue received", latestClosing.revenue, "#18181B"],
                ["− Taxes & statutory", latestClosing.taxes_total, "#F43F5E"],
                ["Net allocable cash", latestClosing.net_allocable, "#18181B"],
                ["→ Next-Year Reserve", latestClosing.reserve_contribution, "#059669"],
                ["→ Founder Savings (30%)", latestClosing.founder_allocation, "#D97706"],
                ["→ Innovation & Growth (70%)", latestClosing.growth_allocation, "#7C3AED"],
                ["Growth expenses", latestClosing.growth_expenses, "#DC2626"],
                ["Unspent I&G carried forward", latestClosing.unspent_growth_balance, "#7C3AED"],
              ].map(([label, amt, color]) => (
                <div key={label} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                  <span className="text-sm text-zinc-500">{label}</span>
                  <span className="font-mono-num text-sm font-semibold" style={{ color }}>{formatINR(Math.abs(amt))}</span>
                </div>
              ))}
              {latestClosing.reserve_shortfall_after > 0 && (
                <p className="text-xs text-red-600 pt-1 flex items-center gap-1"><ShieldAlert size={12} /> Reserve shortfall carried forward: {formatINR(latestClosing.reserve_shortfall_after)}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-zinc-500">No monthly closings yet. Record your first closing from the Monthly Closing tab.</p>
          )}
        </div>

        <div className="card-surface fade-up overflow-hidden lg:col-span-3" data-testid="recent-closings-table">
          <div className="px-6 pt-6 pb-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Recent Monthly Closings</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                  <th className="px-6 py-3 font-medium">Month</th>
                  <th className="px-4 py-3 font-medium text-right">Revenue</th>
                  <th className="px-4 py-3 font-medium text-right">Taxes</th>
                  <th className="px-4 py-3 font-medium text-right">Expenses</th>
                  <th className="px-4 py-3 font-medium text-right">Reserve</th>
                  <th className="px-4 py-3 font-medium text-right">Founder</th>
                  <th className="px-6 py-3 font-medium text-right">Unspent I&G</th>
                </tr>
              </thead>
              <tbody>
                {data.closings.length === 0 && (
                  <tr><td colSpan={7} className="px-6 py-8 text-center text-zinc-500">No closings recorded for {year}</td></tr>
                )}
                {[...data.closings].reverse().map((c) => (
                  <tr key={c.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50 transition-colors">
                    <td className="px-6 py-3 font-medium text-zinc-800">{MONTHS[c.month - 1]}
                      {c.status === "reopened" && <span className="ml-2 text-[10px] uppercase text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">Reopened</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-num text-zinc-800">{formatINR(c.revenue)}</td>
                    <td className="px-4 py-3 text-right font-mono-num text-rose-600">{formatINR(c.taxes_total)}</td>
                    <td className="px-4 py-3 text-right font-mono-num text-zinc-500">{formatINR(c.actual_expenses)}</td>
                    <td className="px-4 py-3 text-right font-mono-num text-emerald-600">{formatINR(c.reserve_contribution)}</td>
                    <td className="px-4 py-3 text-right font-mono-num text-amber-600">{formatINR(c.founder_allocation)}</td>
                    <td className="px-6 py-3 text-right font-mono-num text-zinc-800">{formatINR(c.unspent_growth_balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
